import { DocumentLoadEvent, PageChangeEvent, RenderPage, RenderPageProps, ZoomEvent } from '@react-pdf-viewer/core';
import { useQuery } from '@tanstack/react-query';
import { uniqueId } from 'lodash';
import { ChangeEvent, MouseEvent, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RndDragCallback, RndResizeCallback } from 'react-rnd';
import { useLocation, useParams } from 'react-router-dom';
import { AnyObject } from 'yup';
import { getTestContractSealPdf } from '../../api/contract';
import { getDefaultSizeSealStamp } from '../../api/sealStamp';
import HeaderSaveAction from '../../components/sign/header/HeaderSaveAction';
import TabsTestSign from '../../components/sign/TabsTestSign';
import CardBox from '../../components/ui/containers/CardBox';
import StickyBox from '../../components/ui/containers/StickyBox';
import SignProvider from '../../components/ui/signs/SignProvider';
import SignRenderPage from '../../components/ui/viewer/SignRenderPage';
import Viewer from '../../components/ui/viewer/Viewer';
import { queryKey } from '../../constants/queryKey';
import { useAlert } from '../../hooks/useAlert';
import { pdfData } from '../../model/api/contract';
import { Sign } from '../../model/sign';
import SysConfigStore from '../../store/common/SysConfigStore';

/**
 * 인감 날인 페이지
 * 주의 : Axios로 가져온 pdfAxiosRes의 ArrayBuffer 메모리가 Viewer로 빠지면서,
 * Worker만 접근이 가능해, pdfObject로 따로 상태관리 해야 함
 */
export default function DetailPageNew() {
	const { labelText, isComponentEnabled } = SysConfigStore();
	const { t } = useTranslation();
	const location = useLocation();
	const { sealStampId: paramSignId } = useParams<{ sealStampId?: string }>();
	const sealStampId = paramSignId ?? 'default';

	const [snackbar] = useAlert();
	const [signs, setSigns] = useState<Sign[]>([]);
	const [zoom, setZoom] = useState(1);
	const [pageInfo, setPageInfo] = useState({
		width: 0,
		height: 0,
	});
	const [pdfObject, setPdfObject] = useState<pdfData | null>(null);
	const [goToPageIndex, setGoToPageIndex] = useState<number | null>(null);
	const [saveSize, setSaveSize] = useState(0);
	const [tabValue, setTabValue] = useState(0);
	const [uploadedImage, setUploadedImage] = useState<Sign | null>(null);
	const [initialSize, setInitialSize] = useState<{ width: number; height: number } | null>(null);

	const title = t('modify_seal_stamp_size_with_preview_title').replace('{{인장}}', labelText('seal_stamp'));

	const { data: pdfAxiosRes, isLoading: pdfAxiosLoading } = useQuery(
		queryKey.sealPdfData,
		() => getTestContractSealPdf(),
		{
			onError: error => {
				snackbar(t('contract_warn_no_seal_stamp').replace('{{날인}}', labelText('seal')), 'error');
				window.location.href = '/';
			},
		},
	);

	const loadUploadedImage = (payload: any) => {
		const img = new Image();
		img.onload = () => {
			setUploadedImage({
				id: 'uploaded-image',
				imageUrl: payload.imageUrl,
				name: 'uploaded-image',
				pageIndex: 0,
				x: 100,
				y: 100,
				width: payload.width || img.naturalWidth,
				height: payload.height || img.naturalHeight,
			});
		};
		img.src = payload.imageUrl;
	};

	useEffect(() => {
		const key = `seal_stamp_payload_${sealStampId}`;

		// 1. 기존 window에 준비 완료 알림
		if (window.opener) {
			window.opener.postMessage('READY_FOR_SEAL_PAYLOAD', window.location.origin);
		}

		// 2. postMessage로 payload 수신
		const handleMessage = (event: MessageEvent) => {
			if (event.origin !== window.location.origin) return;

			const { type, payload } = event.data ?? {};
			if (type === 'SEAL_PAYLOAD' && payload?.imageUrl) {
				const payloadSealId = payload.seal_stamp_id || sealStampId;
				const payloadKey = `seal_stamp_payload_${payloadSealId}`;

				try {
					sessionStorage.setItem(payloadKey, JSON.stringify(payload));
					loadUploadedImage(payload);
				} catch (e) {
					snackbar(t('modify_seal_stamp_size_err_cannot_get_stamp_info'), 'error');
				}
			}
		};

		window.addEventListener('message', handleMessage);

		// 3. 새로고침 시 복원
		try {
			const stored = sessionStorage.getItem(key);
			if (stored) {
				const parsed = JSON.parse(stored);
				if (parsed?.imageUrl) {
					loadUploadedImage(parsed);
				}
			}
		} catch (e) {
			snackbar(t('modify_seal_stamp_size_err_cannot_get_stamp_info'), 'error');
		}

		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, [sealStampId]);

	useEffect(() => {
		if (signs.length === 1 && !initialSize) {
			setInitialSize({ width: signs[0].width, height: signs[0].height });
		}
	}, [signs, initialSize]);

	const getCurrentZoomFromDOM = (): number => {
		const zoomEl = document.querySelector('[data-testid="zoom__popover-target-scale"]');
		if (!zoomEl) return 1;

		const text = zoomEl.textContent?.replace('%', '');
		const scale = Number(text);
		return Number.isNaN(scale) ? 1 : scale / 100;
	};

	const handleDragStop: RndDragCallback = useCallback(
		(e, data) => {
			if (!data) return;
			const currentZoom = getCurrentZoomFromDOM();
			const {
				x: prevX,
				y: prevY,
				node: {
					dataset: { id },
				},
			} = data;

			setSigns(prev =>
				prev.map(sign => {
					const { id: signId } = sign;

					const x = prevX / currentZoom;
					const y = prevY / currentZoom;
					return signId !== id ? sign : { ...sign, x, y };
				}),
			);
		},
		[signs],
	);

	// 날인 이미지 사이즈 조정
	const handleResizeStop: RndResizeCallback = useCallback(
		(e, dir, elementRef, delta, position) => {
			const currentZoom = getCurrentZoomFromDOM();
			if (!elementRef || !position) return;
			const {
				offsetWidth: width,
				offsetHeight: height,
				dataset: { id },
			} = elementRef;
			const { x, y } = position;

			setSigns(prev =>
				prev.map(sign => {
					const { id: signId } = sign;
					return signId !== id
						? sign
						: {
								...sign,
								width: width / currentZoom,
								height: height / currentZoom,
								x: x / currentZoom,
								y: y / currentZoom,
						  };
				}),
			);
		},
		[signs],
	);

	// 날인 리스트에서 계약서로 이미지 추가
	const handleDrop = useCallback(
		async (item: AnyObject, dropResult: AnyObject) => {
			const { id, imageUrl, name, width, height, x: cursorX, y: cursorY } = item;
			const { id: pageIndex, viewerZoom, x: pageCursorX, y: pageCursorY, pageWidth, pageHeight } = dropResult;
			const x = pageCursorX - cursorX;
			const y = pageCursorY - cursorY;

			setZoom(viewerZoom);

			setPageInfo({
				width: pageWidth / viewerZoom,
				height: pageHeight / viewerZoom,
			});

			try {
				let defaultWidth: number;
				let defaultHeight: number;

				if (location.pathname.includes('/sign/modify-contract-seal')) {
					const result = await getDefaultSizeSealStamp(
						Number(sealStampId),
						width,
						height,
						isComponentEnabled,
					);
					defaultWidth = result.width;
					defaultHeight = result.height;
				} else {
					defaultWidth = width / viewerZoom;
					defaultHeight = height / viewerZoom;
				}

				setSigns(prev => [
					...prev,
					{
						id: uniqueId(`${id}-`),
						name,
						pageIndex: Number(pageIndex),
						imageUrl,
						x: x / viewerZoom,
						y: y / viewerZoom,
						width: defaultWidth,
						height: defaultHeight,
					},
				]);
			} catch (error) {
				setSigns(prev => [
					...prev,
					{
						id: uniqueId(`${id}-`),
						name,
						pageIndex: Number(pageIndex),
						imageUrl,
						x: x / viewerZoom,
						y: y / viewerZoom,
						width: width / viewerZoom,
						height: height / viewerZoom,
					},
				]);
			}
		},
		[signs, zoom],
	);

	// PDF 확대 축소
	const handleZoom = useCallback(
		(e: ZoomEvent) => {
			const { scale = 1 } = e;
			setZoom(scale);
		},
		[signs, zoom],
	);

	// 날인 이미지 입력으로 사이즈 조정
	const handleChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>, sign: Sign) => {
			const {
				target: { value: v },
			} = e;
			const value = Number(v);
			const { id, width: imageWidth, height: imageHeight } = sign;

			const { width: pageWidth } = pageInfo;

			const minValue = value < 40 ? 40 : value;
			const width = minValue > pageWidth ? pageWidth : minValue;
			const height = (imageHeight * width) / imageWidth;

			setSigns(prev => {
				return [
					...prev.map(item =>
						item.id === id
							? ({
									...item,
									id: uniqueId(`${id.toString().split('-')[0]}-`),
									width,
									height,
							  } as Sign)
							: item,
					),
				];
			});
		},
		[signs, zoom],
	);

	// 날인 이미지 삭제
	const handleDeleteSign = useCallback(
		(e: MouseEvent<HTMLButtonElement>, sign: Sign) => {
			const { id } = sign;
			setSigns(prev => prev.filter(({ id: signId }) => signId !== id));
		},
		[signs, zoom],
	);

	// 날인 이미지 사이즈 저장
	const handleSaveSize = useCallback(
		async (e: MouseEvent<HTMLButtonElement>, sign: Sign) => {
			const { width } = sign;

			setSaveSize(width);
		},
		[signs, zoom, isComponentEnabled],
	);

	// 날인 저장된 이미지 사이즈 불러오기
	const handleLoadSize = useCallback(
		(e: MouseEvent<HTMLButtonElement>, sign: Sign) => {
			const { id, width: imageWidth, height: imageHeight } = sign;
			const { width: pageWidth } = pageInfo;

			const width = Number(saveSize) > pageWidth ? pageWidth : Number(saveSize);
			const height = (imageHeight * width) / imageWidth;

			setSigns(prev => {
				return [
					...prev.map(item =>
						item.id === id
							? ({
									...item,
									id: uniqueId(`${id.toString().split('-')[0]}-`),
									width,
									height,
							  } as Sign)
							: item,
					),
				];
			});
		},
		[signs, zoom],
	);

	// 날인 테스트 페이지에서 크기 되돌리기
	const handleResetSize = useCallback(() => {
		if (!initialSize) return;

		setSigns(prev =>
			prev.map(sign => ({
				...sign,
				width: initialSize.width,
				height: initialSize.height,
				id: uniqueId(`${String(sign.id).split('-')[0]}-`),
			})),
		);
	}, [initialSize]);

	// PDF 페이지 렌더링
	const renderPage: RenderPage = useCallback(
		(props: RenderPageProps) => (
			<SignRenderPage
				signs={signs}
				onDragStop={handleDragStop}
				onResizeStop={handleResizeStop}
				onDeleteSign={handleDeleteSign}
				onChange={handleChange}
				onSaveSize={handleSaveSize}
				onLoadSize={handleLoadSize}
				enableResizing={!isComponentEnabled('CMPNT362_DISABLE_SEAL_RESIZE')}
				{...props}
			/>
		),
		[signs, zoom],
	);

	// PDF 페이지 load 후
	const handleDocumentLoad = async (e: DocumentLoadEvent) => {
		const fileArray = await e.doc.getData();
		const fileName = pdfAxiosRes?.fileName;

		if (fileName) setPdfObject({ file: fileArray, fileName });
	};

	// PDF 페이지 사용자가 변경한 경우
	const handlePageChange = useCallback((e: PageChangeEvent) => setGoToPageIndex(null), []);

	// 날인 탭 이동
	const handleChangeTab = useCallback((_: SyntheticEvent, v: number) => setTabValue(v), [signs, zoom]);

	return (
		<div style={{ backgroundColor: 'rgb(244, 244, 245)' }}>
			<section className='flex flex-col gap-5 p-8 max-w-[1920px] mx-auto'>
				<h2 className='text-xl font-bold mt-2'>{title}</h2>
				<div>
					<SignProvider>
						<div className='flex gap-5'>
							<CardBox className='basis-8/12 p-5'>
								{!pdfAxiosLoading && pdfAxiosRes && (
									<Viewer
										fileUrl={pdfAxiosRes.file}
										goToPageIndex={goToPageIndex}
										onPageChange={handlePageChange}
										renderPage={renderPage}
										onZoom={handleZoom}
										onDocumentLoad={handleDocumentLoad}
									/>
								)}
							</CardBox>
							<div className='basis-4/12'>
								<StickyBox className='grid gap-4'>
									{pdfObject && (
										<HeaderSaveAction
											id={String(sealStampId)}
											signs={signs}
											onReset={handleResetSize}
										/>
									)}
									{uploadedImage && (
										<TabsTestSign
											id={Number(sealStampId)}
											signs={signs}
											value={tabValue}
											onChange={handleChangeTab}
											onDrop={handleDrop}
											uploadedImage={{
												id: 'temp-id',
												title: uploadedImage?.name,
												imageUrl: uploadedImage?.imageUrl,
												canDrag: true,
											}}
										/>
									)}
								</StickyBox>
							</div>
						</div>
					</SignProvider>
				</div>
			</section>
		</div>
	);
}
